import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Strip common file extensions (.pdf, .xlsx, etc.) from display titles */
export function stripFileExt(str: string): string {
  if (!str) return str;
  return str.replace(/\.(pdf|xlsx|xls|docx|doc|pptx|ppt)$/i, "");
}

/**
 * Nomes próprios que sobrevivem ao sentence case dos títulos: dias, festas,
 * cozinhas e lugares, queijos e molhos com nome, os pratos batizados. Sem
 * isto "The Sunday Loaf" virava "The sunday loaf" e "The Fourth of July Bun",
 * "The fourth of july bun" — os heróis nomeados nas copies do Clean Bakes e
 * do Clean Meals perdiam o nome na lista.
 */
const NOMES_PROPRIOS = new Set([
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
  "january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december",
  "thanksgiving", "christmas", "easter", "halloween", "hanukkah", "valentine", "valentine's",
  "american", "english", "french", "italian", "greek", "mexican", "spanish", "cuban", "german", "swiss", "belgian", "canadian",
  "irish", "polish", "korean", "thai", "japanese", "chinese", "indian", "mediterranean", "tuscan", "southern", "cajun", "creole",
  "tex-mex", "asian", "lebanese", "moroccan", "turkish", "portuguese", "brazilian", "argentine", "peruvian", "hawaiian", "californian",
  "denver", "philly", "philadelphia", "nashville", "buffalo", "kentucky", "louisiana", "texas", "california", "york", "orleans", "england",
  "monterey", "jack", "gruyere", "gruyère", "parmesan", "kalamata", "dijon", "worcestershire", "tabasco", "sriracha", "chipotle",
  "benedict", "florentine", "caprese", "cobb", "reuben", "caesar", "ranch", "bolognese", "alfredo", "lorraine", "wellington", "provençal",
  "diner", "dough",
]);
const SIGLAS = /^[A-Z]{2,4}$/;
const DEPOIS_DE_NEW = ["york", "orleans", "england", "mexico", "jersey"];

/**
 * Converts a string to sentence case: first letter uppercase, rest lowercase —
 * keeping proper nouns (days, holidays, cuisines, named dishes), short
 * all-caps acronyms (BLT, PB, NY) and hyphenated names written with capitals
 * ("Head-of-the-Table") the way they came.
 */
export function toSentenceCase(str: string): string {
  if (!str) return str;
  const palavras = str.split(" ");
  const nucleo = (p: string) => p.replace(/^[^A-Za-zÀ-ÿ0-9]+|[^A-Za-zÀ-ÿ0-9']+$/g, "");
  // Prato batizado ("The Sunday Loaf", "The Head-of-the-Table Roast"): começa
  // com "The" e cada palavra de peso vem com maiúscula. Fica como está.
  const LIGACAO = new Set(["of", "the", "a", "an", "and", "with", "on", "in", "for", "to", "de", "y"]);
  const batizado = palavras.length >= 3 && palavras[0] === "The" &&
    palavras.slice(1).every((p) => { const n = nucleo(p); return !n || LIGACAO.has(n.toLowerCase()) || /^[A-Z0-9]/.test(n); });
  if (batizado) return str;
  return palavras
    .map((palavra, i) => {
      if (!palavra) return palavra;
      const limpa = nucleo(palavra);
      if (i === 0) return palavra.charAt(0).toUpperCase() + (SIGLAS.test(limpa) ? palavra.slice(1) : palavra.slice(1).toLowerCase());
      if (SIGLAS.test(limpa)) return palavra;
      if (/-/.test(limpa) && /[A-Z]/.test(limpa.slice(1))) return palavra;
      const minuscula = limpa.toLowerCase();
      const anterior = nucleo(palavras[i - 1] || "").toLowerCase();
      const seguinte = nucleo(palavras[i + 1] || "").toLowerCase();
      const proprio = NOMES_PROPRIOS.has(minuscula) ||
        (minuscula === "new" && DEPOIS_DE_NEW.includes(seguinte)) ||
        (anterior === "new" && DEPOIS_DE_NEW.includes(minuscula));
      const baixa = palavra.toLowerCase();
      if (!proprio) return baixa;
      const inicio = baixa.search(/[a-zà-ÿ]/);
      return inicio < 0 ? baixa : baixa.slice(0, inicio) + baixa.charAt(inicio).toUpperCase() + baixa.slice(inicio + 1);
    })
    .join(" ");
}
